<template>
  <div>
    <div class="sonar-progress__wrapper">
      <div class="sonar-progress-circle" :data-progress="scoreAvg">
        <div>
          <div class="full sonar-progress-circle__slice">
            <div class="sonar-progress-circle__fill" />
          </div>
          <div class="sonar-progress-circle__slice">
            <div class="sonar-progress-circle__fill" />
            <div
              class="sonar-progress-circle__fill sonar-progress-circle__bar"
            />
          </div>
        </div>
        <div class="sonar-progress-circle__overlay" />
      </div>
      <div class="sonar-progress__outer-circle">
        <div
          class="sonar-progress__dot-container sonar-progress__dot-container--low"
        >
          <div
            class="sonar-progress__dot-wrapper sonar-progress__dot-wrapper--low"
          >
            <div
              v-tooltip="{
                content: `Low: ${lowPercentage}`,
                placement: 'top-center',
              }"
              :style="`background: ${learningAreaColor};`"
              class="sonar-progress__dot sonar-progress__dot--low"
            />
          </div>
        </div>
        <div class="sonar-progress__dot-container">
          <div
            class="sonar-progress__dot-wrapper sonar-progress__dot-wrapper--high"
          >
            <div
              v-tooltip="{
                content: `High: ${highPercentage}`,
                placement: 'top-center',
              }"
              :style="`background: ${learningAreaColor};`"
              class="sonar-progress__dot sonar-progress__dot--high"
            />
          </div>
        </div>
        <div
          class="sonar-progress__dot-container sonar-progress__dot-container--avg"
        >
          <div
            class="sonar-progress__dot-wrapper sonar-progress__dot-wrapper--avg"
          >
            <div
              v-tooltip="{
                content: `Avg: ${avgPercentage}`,
                placement: 'top-center',
              }"
              class="sonar-progress__dot sonar-progress__dot--avg"
            />
          </div>
        </div>
        <div class="sonar-progress__green-scanner" />
        <div
          class="sonar-progress__green-scanner sonar-progress__green-scanner--dots"
        />
        <div
          class="sonar-progress__green-scanner sonar-progress__green-scanner--offset"
        />
        <div
          class="sonar-progress__green-scanner sonar-progress__green-scanner--total"
        />
        <div class="sonar-progress__graph-icon">
          <component :is="iconComponent"></component>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getLearningAreaSnipeColor } from '@/helpers'
import CONSTANTS from '@/constants'
import CyberSecurity from '../assets/images/learning-areas/cybersecurity-round.svg'
import Networking from '../assets/images/learning-areas/networking-round.svg'
import Cloud from '../assets/images/learning-areas/cloud-round.svg'

export default {
  name: 'SonarProgressCard',
  components: {
    CyberSecurityIcon: { ...CyberSecurity, name: 'CyberSecurityIcon' },
    NetworkingIcon: { ...Networking, name: 'NetworkingIcon' },
    CloudIcon: { ...Cloud, name: 'CloudIcon' },
  },
  props: {
    currentScore: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      scoreAvg: 0,
      turnTime: 3,
    }
  },
  computed: {
    iconComponent() {
      const icons = {
        'cyber-security': 'CyberSecurityIcon',
        networking: 'NetworkingIcon',
        cloud: 'CloudIcon',
      }
      return icons[this.currentScore.slug] || null
    },
    learningAreaColor() {
      return getLearningAreaSnipeColor(
        CONSTANTS.SONAR.LEARNING_AREAS['Cyber Security'].name
      )
    },
    turnTimeSecH() {
      const { highest_score } = this.currentScore.user_status
      return Number(this.turnTime * Number(highest_score))
    },
    turnTimeSecL() {
      const { lowest_score } = this.currentScore.user_status
      return Number(this.turnTime * Number(lowest_score))
    },
    turnTimeSecA() {
      const { score_average } = this.currentScore.user_status
      return Number(this.turnTime * Number(score_average))
    },
    turnTimeSecDelay() {
      return Number(this.turnTime + Number(this.turnTimeSecH))
    },
    turnTimeSecOffset() {
      return this.turnTime - Number(this.turnTimeSecH)
    },
    avgPercentage() {
      return `${(this.currentScore.user_status.score_average * 100).toFixed(
        0
      )}%`
    },
    highPercentage() {
      return `${(this.currentScore.user_status.highest_score * 100).toFixed(
        0
      )}%`
    },
    lowPercentage() {
      return `${(this.currentScore.user_status.lowest_score * 100).toFixed(0)}%`
    },
  },
  mounted() {
    this.scoreAverage()
    this.setRootCSSVars()
  },
  methods: {
    scoreAverage() {
      setTimeout(() => {
        this.scoreAvg = this.currentScore.user_status.score_average * 100
      }, 500)
    },
    setRootCSSVars() {
      const root = document?.documentElement
      if (!root) return
      const {
        lowest_score,
        highest_score,
        score_average,
      } = this.currentScore.user_status
      root.style.setProperty('--low-dot-turn', `${lowest_score}turn`)
      root.style.setProperty('--high-dot-turn', `${highest_score}turn`)
      root.style.setProperty('--avg-dot-turn', `${score_average}turn`)
      root.style.setProperty('--avg-dot', `${score_average * 100}`)
      root.style.setProperty('--high-dot-perc', `${highest_score * 100}%`)
      root.style.setProperty('--low-dot-perc', `${lowest_score * 100}%`)
      root.style.setProperty(
        '--diff-perc',
        `${(highest_score - lowest_score) * 100}%`
      )
      root.style.setProperty('--high-dot-sec', `${this.turnTimeSecH}s`)
      root.style.setProperty('--low-dot-sec', `${this.turnTimeSecL}s`)
      root.style.setProperty('--avg-dot-sec', `${this.turnTimeSecA}s`)
      root.style.setProperty('--tradar-sec', `${this.turnTimeSecOffset}s`)
      root.style.setProperty('--tradar-delay', `${this.turnTimeSecDelay}s`)
    },
  },
}
</script>

<style lang="scss" scoped>
$circle-size: 134px;
$circle-size-desktop: 167px;
$circle-background: #743eb3;
$circle-color: #9f69e1;
$inset-size: 118px;
$inset-size-desktop: 151px;
$inset-color: #34495e;
$transition-delay: 5.5s;

.sonar-progress {
  &__tooltip {
    position: relative;
    z-index: 1000;
  }
  &__graph-icon {
    background: #2c0e46;
    position: absolute;
    z-index: 2;
    top: 25%;
    left: 25%;
    border-radius: 50%;
    width: 60px;
    height: 60px;
    @include tablet {
      width: 76px;
      height: 76px;
    }
    svg {
      width: 60px;
      height: 60px;
      @include tablet {
        width: 76px;
        height: 76px;
      }
    }
  }
  &__wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    background-color: #743eb3;
    position: relative;
    width: 134px;
    height: 134px;
    @include tablet {
      height: 167px;
      width: 167px;
    }
  }
  &__outer-circle {
    position: relative;
    border-radius: 50%;
    background: #2c0e46;
    width: 118px;
    height: 118px;
    @include tablet {
      height: 151px;
      width: 151px;
    }
  }

  &__green-scanner,
  &__green-scanner--dot {
    opacity: 0;
    border-radius: 50%;
    position: absolute;
    top: -8px;
    left: -8px;
    background: conic-gradient(
      rgba(187, 133, 255, 0.5) 10%,
      rgba(187, 133, 255, 0) 25%
    );
    animation: scan 3s linear;
    animation-fill-mode: forwards;

    width: 134px;
    height: 134px;
    @include tablet {
      height: 167px;
      width: 167px;
    }
    &--dots {
      animation: scan-dots var(--high-dot-sec) linear;
      animation-delay: 3s;
    }
    &--offset {
      transform: rotate(var(--high-dot-turn));
      animation: scan-dots-offset var(--tradar-sec) linear;
      animation-delay: var(--tradar-delay);
    }
    &--total {
      transform: rotate(var(--low-dot-turn));
      animation: scan-dots-total 0.1s linear;
      animation-delay: var(--tradar-delay);
      animation-fill-mode: forwards;
      background: conic-gradient(
        rgba(187, 133, 255, 0.5) var(--low-dot-perc),
        rgba(187, 133, 255, 0) var(--high-dot-perc)
      );
    }
  }

  &__outer-circle:after {
    animation-delay: 1s;
  }

  &__dot-container {
    position: absolute;
    height: 116px;
    left: calc(50%);
    top: 1px;
    transform: translateX(-50%);
    z-index: 200;
    cursor: pointer;
    @include tablet {
      height: 149px;
    }
    &--low {
      z-index: 300;
    }
    &--avg {
      z-index: 150;
      top: 1px;
      @include tablet {
        height: 150px;
        top: 1px;
      }
    }
  }
  &__dot-wrapper {
    position: relative;
    z-index: 100;
    height: 100%;
    opacity: 0;
    &--low {
      animation: low-dot var(--low-dot-sec) linear;
      animation-delay: 3s;
      animation-fill-mode: forwards;
    }
    &--high {
      animation: high-dot var(--high-dot-sec) linear;
      animation-delay: 3s;
      animation-fill-mode: forwards;
    }
    &--avg {
      animation: avg-dot var(--avg-dot-sec) ease-out 6s forwards;
      .sonar-progress__dot {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 20px;
        height: 20px;
        border: 5px solid #bb85ff91;
        top: -15px;
        @include tablet {
          top: -14px;
        }
        &::before {
          position: absolute;
          content: '';
          width: 10px;
          height: 10px;
          background: #b37ef4;
          border-radius: 50%;
        }
      }
    }
  }
  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-left: auto;
    position: relative;
    top: -9px;
    z-index: 300;
    &--low {
      z-index: 2000;
    }
  }

  &-circle {
    margin: 20px auto;
    width: $circle-size;
    height: $circle-size;
    position: absolute;
    background-color: $circle-background;
    border-radius: 50%;
    @include tablet {
      width: $circle-size-desktop;
      height: $circle-size-desktop;
    }
    .sonar-progress-circle__slice,
    .sonar-progress-circle__fill {
      width: $circle-size;
      height: $circle-size;
      position: absolute;
      backface-visibility: hidden;
      transition: transform var(--avg-dot-sec) ease-out $transition-delay;
      border-radius: 50%;
      @include tablet {
        width: $circle-size-desktop;
        height: $circle-size-desktop;
      }
    }
    .sonar-progress-circle__slice {
      clip: rect(0px, $circle-size, $circle-size, $circle-size/2);
      .sonar-progress-circle__fill {
        clip: rect(0px, $circle-size/2, $circle-size, 0px);
        background-color: $circle-color;
      }
      @include tablet {
        clip: rect(
          0px,
          $circle-size-desktop,
          $circle-size-desktop,
          $circle-size-desktop/2
        );
        .sonar-progress-circle__fill {
          clip: rect(0px, $circle-size-desktop/2, $circle-size-desktop, 0px);
          background-color: $circle-size-desktop;
        }
      }
    }
    .sonar-progress-circle__overlay {
      width: $inset-size;
      height: $inset-size;
      position: absolute;
      margin-left: ($circle-size - $inset-size)/2;
      margin-top: ($circle-size - $inset-size)/2;
      background-color: $inset-color;
      border-radius: 50%;
      @include tablet {
        margin-left: ($circle-size-desktop - $inset-size-desktop)/2;
        margin-top: ($circle-size-desktop - $inset-size-desktop)/2;
        width: $inset-size-desktop;
        height: $inset-size-desktop;
      }
    }

    $i: 0;
    $increment: 180deg / 100;
    @while $i <= 100 {
      &[data-progress='#{$i}'] {
        .sonar-progress-circle__slice.full,
        .sonar-progress-circle__fill {
          transform: rotate($increment * $i);
        }
        .sonar-progress-circle__fill.sonar-progress-circle__bar {
          transform: rotate($increment * $i * 2);
        }
        $i: $i + 1;
      }
    }
  }

  @keyframes high-dot {
    from {
      opacity: 0.5;
    }
    to {
      opacity: 1;
      transform: rotate(var(--high-dot-turn));
    }
  }
  @keyframes low-dot {
    from {
      opacity: 0.5;
    }
    to {
      opacity: 1;
      transform: rotate(var(--low-dot-turn));
    }
  }
  @keyframes avg-dot {
    from {
      opacity: 0;
    }
    90% {
      opacity: 0;
    }
    to {
      opacity: 1;
      transform: rotate(var(--avg-dot-turn));
    }
  }
  @keyframes scan {
    from {
      opacity: 1;
    }
    95% {
      opacity: 1;
    }
    to {
      opacity: 0;
      transform: rotate(1turn);
    }
  }

  @keyframes scan-dots {
    from {
      opacity: 1;
      transform: rotate(0);
    }
    85% {
      opacity: 1;
    }
    to {
      opacity: 0;
      transform: rotate(var(--high-dot-turn));
    }
  }

  @keyframes scan-dots-offset {
    from {
      opacity: 0;
      transform: rotate(var(--high-dot-turn));
    }
    20% {
      opacity: 1;
    }
    85% {
      opacity: 1;
    }
    to {
      opacity: 0;
      transform: rotate(359deg);
    }
  }

  @keyframes avg-dot-progress {
    from {
      transform: rotate(0deg);
      opacity: 1;
    }
    to {
      opacity: 1;
      transform: rotate(var(--avg-dot-turn));
    }
  }

  @keyframes avg-dot-progress-more {
    from {
      transform: rotate(0deg);
      opacity: 1;
      border-color: #9f69e1;
    }
    to {
      opacity: 1;
      border-color: #9f69e1;

      transform: rotate(180deg);
    }
  }
  @keyframes scan-dots-total {
    from {
      opacity: 0;
    }
    90% {
      opacity: 0;
    }
    to {
      opacity: 1;
      background: conic-gradient(
        rgba(187, 133, 255, 0.5) 0%,
        rgba(187, 133, 255, 0) var(--diff-perc)
      );
    }
  }

  @keyframes ripple {
    to {
      transform: scale(2.5);
    }
  }
}
</style>
